// http/useApi.ts
import { api } from './apiClient';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient, QueryKey } from 'react-query';

/**
 * Bevisst "slapp" typing på props (any) for å matche alt eksisterende rq / rqPost / rqDel.
 * Målet nå: få bygget til å fungere uten å endre hundrevis av kall.
 */

/* ------------------ GET ------------------ */

export const useGet = (props: any) => {
  const { url, keys, cacheQuery, enabled = true } = props;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useQuery(
    keys as QueryKey,
    async () => {
      const { data } = await api.get(url);
      return data;
    },
    {
      enabled,
      initialData: () => {
        if (!cacheQuery) return undefined;

        const cacheData: any = queryClient.getQueryData(cacheQuery.keys);
        if (!cacheData) return undefined;

        const key0 = Array.isArray(keys) ? keys[0] : keys;

        switch (key0 || '') {
          case 'company':
            return cacheData?.companies?.find(
              (c: any) => c.id === cacheQuery.id
            );
          case 'department':
            return cacheData?.departments?.find(
              (d: any) => d.id === cacheQuery.id
            );
          case 'employee':
            return cacheData?.employees?.find(
              (e: any) => e.id === cacheQuery.id
            );
          case 'package':
            return cacheData?.productPackages?.find(
              (p: any) => p.id === cacheQuery.id
            );
          case 'product':
            if (Array.isArray(cacheData?.products)) {
              return cacheData.products.find(
                (p: any) => p.id === cacheQuery.id
              );
            }
            return undefined;
          case 'ticket':
            return cacheData?.tickets?.find((t: any) => t.id === cacheQuery.id);
          case 'ticketPost':
            return cacheData?.ticketPosts?.find(
              (t: any) => t.id === cacheQuery.id
            );
          default:
            return cacheData;
        }
      },
      onError: (error: any) => {
        const key0 = Array.isArray(keys) ? keys[0] : keys;
        enqueueSnackbar(`Could not retrieve ${key0}`, {
          variant: 'error',
        });
        return error;
      },
    }
  );
};

/* ------------------ POST ------------------ */

export const usePost = (props: any) => {
  const { url, name, keys } = props;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    async (formData: any) => {
      const { data } = await api.post(url, formData);
      return data;
    },
    {
      onSuccess: (res: any) => {
        if (keys) {
          const k: any = keys;
          if (Array.isArray(k) || typeof k === 'string') {
            queryClient.invalidateQueries(k as QueryKey);
          } else if (k.all) {
            queryClient.invalidateQueries(k.all as QueryKey);
          }
        }

        enqueueSnackbar(`${name ?? 'Request'} success`, {
          variant: 'success',
          autoHideDuration: 5000,
        });
      },
      onError: (error: any) => {
        console.log('usePost error', error);
        enqueueSnackbar(`Could not perform request: ${error.response?.data}`, {
          variant: 'error',
          autoHideDuration: 5000,
        });
      },
    }
  );
};

/* ------------------ PUT ------------------ */

export const usePut = (props: any) => {
  const { url, name, keys } = props;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    async (formData: any) => {
      const { data } = await api.put(url, formData);
      return data;
    },
    {
      onSuccess: (data: any) => {
        if (keys) {
          const k: any = keys;

          if (Array.isArray(k) || typeof k === 'string') {
            queryClient.invalidateQueries(k as QueryKey);
          } else {
            if (k.all) {
              queryClient.invalidateQueries(k.all as QueryKey);
            }
            if (k.single) {
              queryClient.setQueryData(k.single as QueryKey, data);
            }
          }
        }

        enqueueSnackbar(`${name ?? 'Resource'} updated`, {
          variant: 'success',
          autoHideDuration: 5000,
        });
      },
      onError: (error: any) => {
        enqueueSnackbar(
          `Could not update ${name ?? 'resource'}. ${error.response?.data}`,
          {
            variant: 'error',
            autoHideDuration: 5000,
          }
        );
      },
    }
  );
};

/* ------------------ DELETE ------------------ */

export const useDel = (props: any) => {
  const { name, keys, url } = props;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    async (formData: any) => {
      const resolvedUrl =
        formData && formData.id ? url.replace('undefined', formData.id) : url;
      const { data } = await api.delete(resolvedUrl);
      return data;
    },
    {
      onSuccess: () => {
        if (keys) {
          const k: any = keys;
          if (Array.isArray(k) || typeof k === 'string') {
            queryClient.invalidateQueries(k as QueryKey);
          } else if (k.all) {
            queryClient.invalidateQueries(k.all as QueryKey);
          }
        }

        enqueueSnackbar(`Succesfully removed`, {
          variant: 'success',
          autoHideDuration: 5000,
        });
      },
      onError: () => {
        enqueueSnackbar(`Could not delete ${name ?? 'item'}`, {
          variant: 'error',
        });
      },
    }
  );
};
